<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import BaseButton from '@/components/common/BaseButton.vue'
import MobileNavDrawer from './MobileNavDrawer.vue'
import { useAuth } from '@/composables/useAuth'

const open = ref(false)
const router = useRouter()
const { isAuthenticated, role, logout } = useAuth()

// A 'guest' lands on the player dashboard, same as a real 'player'/'coach',
// because that is the only other dashboard actually built (coach's own is
// still commented out in router/index.ts).
//
// 'guest' is not a rare state: the role lives in a custom claim set by
// setUserRole, which did not exist for the project's first months, so every
// account approved before it — and anyone never approved since — carries no
// claim at all. Measured in production 2026-09-18: 1,789 of 2,161 Auth
// accounts have a role claim, 372 do not. (An earlier version of this comment
// said claims "aren't set by anything yet", which stopped being true when
// setUserRole shipped.)
//
// Admin/staff get their own panels; admin-viewer (read-only admin, see
// stores/auth.ts's UserRole comment) routes the same as a real admin — same
// nav, same pages, just read-only.
const dashboardLink = computed(() => {
  if (role.value === 'admin' || role.value === 'admin-viewer') return '/admin/dashboard'
  if (role.value === 'staff') return '/staff/courts'
  return '/player/dashboard'
})
const showDashboard = computed(() => isAuthenticated.value)

async function handleLogout() {
  open.value = false
  await logout()
  await router.push('/')
}
</script>

<template>
  <header class="z-30 w-full">
    <nav class="flex items-center justify-between px-6 py-5 md:px-14 bg-teal bg-grain">
      <!-- Logo -->
      <div class="flex items-center gap-4 shrink-0">
        <RouterLink to="/" class="flex items-center gap-3 shrink-0">
          <img src="@assets/logo/logo.svg" alt="Homecourt Iloilo" class="w-9 h-auto block" />
          <span class="hidden lg:inline font-primary font-extrabold text-cream text-xl tracking-wide">HOMECOURT</span>
        </RouterLink>
        <BaseButton to="/court-status" variant="outline-light" size="sm" class="hidden lg:inline-flex">
          Live Status
        </BaseButton>
      </div>

      <!-- Desktop CTA -->
      <div class="hidden lg:flex items-center gap-3">
        <template v-if="isAuthenticated">
          <BaseButton v-if="showDashboard" :to="dashboardLink" variant="outline-light" size="sm">
            Dashboard
          </BaseButton>
          <BaseButton variant="invert" size="sm" view-only-safe @click="handleLogout">Logout</BaseButton>
        </template>
        <template v-else>
          <BaseButton to="/login" variant="outline-light" size="sm">Login</BaseButton>
          <BaseButton to="/register" variant="invert" size="sm">Register</BaseButton>
        </template>
      </div>

      <!-- Mobile: Dashboard shortcut + toggle -->
      <div class="lg:hidden flex items-center gap-2">
        <BaseButton
          v-if="showDashboard"
          :to="dashboardLink"
          variant="outline-light"
          size="sm"
          class="shrink"
        >
          Dashboard
        </BaseButton>
        <button
          class="flex flex-col gap-1.5 p-2"
          aria-label="Toggle menu"
          @click="open = !open"
        >
          <span class="block h-0.5 w-6 bg-cream" />
          <span class="block h-0.5 w-6 bg-cream" />
          <span class="block h-0.5 w-4 bg-cream" />
        </button>
      </div>
    </nav>

    <!-- Mobile drawer -->
    <MobileNavDrawer v-model="open">
      <RouterLink to="/" class="flex items-center gap-3" @click="open = false">
        <img src="@assets/logo/logo.svg" alt="Homecourt Iloilo" class="w-8 h-auto block" />
        <span class="font-primary font-extrabold text-cream text-lg tracking-wide">HOMECOURT</span>
      </RouterLink>
      <BaseButton to="/court-status" variant="plain-light" size="sm" class="self-start" @click="open = false">
        Live Status
      </BaseButton>
      <template v-if="isAuthenticated">
        <BaseButton variant="invert" size="sm" class="self-start" view-only-safe @click="handleLogout">
          Logout
        </BaseButton>
      </template>
      <template v-else>
        <BaseButton to="/login" variant="plain-light" size="sm" class="self-start" @click="open = false">
          Login
        </BaseButton>
        <BaseButton to="/register" variant="invert" size="sm" class="self-start" @click="open = false">
          Register
        </BaseButton>
      </template>
    </MobileNavDrawer>
  </header>
</template>

<style scoped></style>
